import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Smartphone, Wifi, WifiOff, BarChart3, TrendingUp } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface PWAStats {
  totalInstalls: number;
  installsThisWeek: number;
  installsThisMonth: number;
  activeSessions: number;
  offlineSessions: number;
  totalPageViews: number;
  totalCalculatorUses: number;
  avgSessionDuration: number;
}

export function PWAAnalytics() {
  const [stats, setStats] = useState<PWAStats>({
    totalInstalls: 0,
    installsThisWeek: 0,
    installsThisMonth: 0,
    activeSessions: 0,
    offlineSessions: 0,
    totalPageViews: 0,
    totalCalculatorUses: 0,
    avgSessionDuration: 0
  });
  const [recentInstalls, setRecentInstalls] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPWAStats();
  }, []);

  const fetchPWAStats = async () => {
    try {
      setIsLoading(true);

      // Get total installs
      const { count: totalInstalls } = await supabase
        .from('pwa_installs')
        .select('*', { count: 'exact', head: true });

      // Get installs this week
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const { count: installsThisWeek } = await supabase
        .from('pwa_installs')
        .select('*', { count: 'exact', head: true })
        .gte('installed_at', weekAgo.toISOString());

      // Get installs this month
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      const { count: installsThisMonth } = await supabase
        .from('pwa_installs')
        .select('*', { count: 'exact', head: true })
        .gte('installed_at', monthAgo.toISOString());

      // Get recent installs for table
      const { data: recentInstallsData } = await supabase
        .from('pwa_installs')
        .select('*')
        .order('installed_at', { ascending: false })
        .limit(10);

      // Get session stats
      const { data: sessions } = await supabase
        .from('pwa_sessions')
        .select('*');

      let offlineSessionCount = 0;
      let totalPageViews = 0;
      let totalCalculatorUses = 0;
      let totalDuration = 0;
      let sessionsWithDuration = 0;

      if (sessions) {
        sessions.forEach(session => {
          if (session.is_offline) offlineSessionCount++;
          totalPageViews += session.page_views || 0;
          totalCalculatorUses += session.calculators_used || 0;
          
          if (session.session_end && session.session_start) {
            const duration = new Date(session.session_end).getTime() - new Date(session.session_start).getTime();
            totalDuration += duration;
            sessionsWithDuration++;
          }
        });
      }

      const avgDuration = sessionsWithDuration > 0 ? totalDuration / sessionsWithDuration / 1000 / 60 : 0;

      setStats({
        totalInstalls: totalInstalls || 0,
        installsThisWeek: installsThisWeek || 0,
        installsThisMonth: installsThisMonth || 0,
        activeSessions: sessions?.length || 0,
        offlineSessions: offlineSessionCount,
        totalPageViews,
        totalCalculatorUses,
        avgSessionDuration: avgDuration
      });

      setRecentInstalls(recentInstallsData || []);
    } catch (error) {
      console.error('Error fetching PWA stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading PWA analytics...</div>;
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="installs">Installs</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Installs</CardTitle>
                <Smartphone className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalInstalls}</div>
                <p className="text-xs text-muted-foreground">
                  +{stats.installsThisWeek} this week
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Installs This Month</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.installsThisMonth}</div>
                <p className="text-xs text-muted-foreground">Last 30 days</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Sessions</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.activeSessions}</div>
                <p className="text-xs text-muted-foreground">Total sessions tracked</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Offline Sessions</CardTitle>
                <WifiOff className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.offlineSessions}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.activeSessions > 0 
                    ? `${Math.round((stats.offlineSessions / stats.activeSessions) * 100)}% offline`
                    : '0% offline'}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>User Engagement</CardTitle>
                <CardDescription>Overall usage metrics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total Page Views</span>
                  <span className="text-lg font-bold">{stats.totalPageViews}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total Calculator Uses</span>
                  <span className="text-lg font-bold">{stats.totalCalculatorUses}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Avg Session Duration</span>
                  <span className="text-lg font-bold">{stats.avgSessionDuration.toFixed(1)} min</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Offline Capability</CardTitle>
                <CardDescription>PWA offline performance</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Wifi className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Online Sessions</span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${stats.activeSessions > 0 ? ((stats.activeSessions - stats.offlineSessions) / stats.activeSessions) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-bold">
                    {stats.activeSessions - stats.offlineSessions}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <WifiOff className="h-4 w-4 text-orange-500" />
                      <span className="text-sm">Offline Sessions</span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className="bg-orange-500 h-2 rounded-full" 
                        style={{ width: `${stats.activeSessions > 0 ? (stats.offlineSessions / stats.activeSessions) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-bold">{stats.offlineSessions}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="installs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Installs</CardTitle>
              <CardDescription>Latest PWA installations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentInstalls.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No installs tracked yet</p>
                ) : (
                  <div className="space-y-2">
                    {recentInstalls.map((install) => (
                      <div key={install.id} className="flex justify-between items-center border-b pb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Smartphone className="h-4 w-4" />
                            <span className="text-sm font-medium">{install.device_type || 'Unknown'}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{install.platform}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">
                            {new Date(install.installed_at).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(install.installed_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="engagement" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Page Views per Session</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.activeSessions > 0 
                    ? (stats.totalPageViews / stats.activeSessions).toFixed(1)
                    : '0'}
                </div>
                <p className="text-xs text-muted-foreground">Average per session</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Calculators per Session</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.activeSessions > 0 
                    ? (stats.totalCalculatorUses / stats.activeSessions).toFixed(1)
                    : '0'}
                </div>
                <p className="text-xs text-muted-foreground">Average per session</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Session Duration</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.avgSessionDuration.toFixed(1)} min</div>
                <p className="text-xs text-muted-foreground">Average duration</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
